/**
 * Cupones en palabras y números: el formulario, la validación que se hace antes de mandar (la misma que el backend, que
 * es el que manda), los avisos en vivo y cómo se lee cada cupón. Puro y testeado (`cupones.test.ts`).
 */

import { leerPesos, leerPorcentaje, pesosParaEditar } from "../../descuentos/logic/descuentos";
import { fecha, pesos } from "../../segmentaciones/logic/segmentar";
import type { AplicaA, Aviso, Cupon, CuponParaGuardar, Vigencia } from "../types";

export const R_CUPONES = "/activa/cupones";

export const CODIGO_MIN = 3;
export const CODIGO_MAX = 20;
export const DIAS_MAX = 90;
export const NO_INCLUYE_MAX = 200;
export const DIAS_POR_DEFECTO = 7;

export const TEXTO_AVISO: Record<Aviso, string> = {
  adivinable: "Es fácil de adivinar: si circula, lo puede usar alguien del grupo de control y la medición se arruina. «Generar» le suma 4 caracteres al azar.",
  pasa_el_maximo: "Es más que tu descuento máximo: Mensajes no lo va a ofrecer.",
  vencido: "Ya venció.",
};

/** El código como se guarda: sin espacios en las puntas y en mayúsculas. */
export const normalizarCodigo = (v: string) => String(v ?? "").trim().toUpperCase();

/** Por qué un código no sirve, o null si sirve (la misma regla que el backend). */
export function problemaDelCodigo(codigo: string): string | null {
  if (!codigo) return "Escribí el código como está en tu sistema de pedidos, o tocá «Generar».";
  if (codigo.length < CODIGO_MIN || codigo.length > CODIGO_MAX) return `El código va de ${CODIGO_MIN} a ${CODIGO_MAX} letras o números.`;
  if (/\s/.test(codigo)) return "El código va sin espacios: escribilo como está en tu sistema de pedidos.";
  if (!/^[A-Z0-9-]+$/.test(codigo)) return "El código lleva sólo letras sin tilde, números y guiones.";
  if (!/[A-Z0-9]/.test(codigo)) return "El código necesita al menos una letra o un número.";
  return null;
}

/**
 * Lo que se puede avisar mientras se escribe el código: un espacio o un carácter que no va. Lo corto o vacío no, que
 * todavía lo están escribiendo; eso lo dice «Guardar». Antes el espacio se avisaba recién al guardar.
 */
export function problemaAlEscribir(escrito: string): string | null {
  const codigo = normalizarCodigo(escrito);
  if (!codigo) return null;
  return /\s/.test(codigo) || !/^[A-Z0-9-]+$/.test(codigo) ? problemaDelCodigo(codigo) : null;
}

/** Un código que se adivina: sin ningún número, o con menos de 6 letras y números. */
export function esAdivinable(codigo: string): boolean {
  const limpio = codigo.replace(/-/g, "");
  return !/\d/.test(limpio) || limpio.length < 6;
}

export interface Formulario {
  codigo: string;
  /** el código lo armó «Generar» y no se tocó después */
  generado: boolean;
  descuentoPct: string;
  aplicaA: AplicaA;
  compraMinima: string;
  tipoVigencia: Vigencia["tipo"];
  dias: string;
  desde: string;
  hasta: string;
  noIncluye: string;
}

export function formularioVacio(hoy: string): Formulario {
  return {
    codigo: "",
    generado: false,
    descuentoPct: "",
    aplicaA: "pedido",
    compraMinima: "",
    tipoVigencia: "dias",
    dias: String(DIAS_POR_DEFECTO),
    desde: hoy,
    hasta: "",
    noIncluye: "",
  };
}

export function formularioDe(c: Cupon, hoy: string): Formulario {
  return {
    codigo: c.codigo,
    generado: c.generado,
    descuentoPct: String(c.descuentoPct),
    aplicaA: c.aplicaA,
    compraMinima: pesosParaEditar(c.compraMinima),
    tipoVigencia: c.vigencia.tipo,
    dias: c.vigencia.tipo === "dias" ? String(c.vigencia.dias) : String(DIAS_POR_DEFECTO),
    desde: c.vigencia.tipo === "fechas" ? c.vigencia.desde : hoy,
    hasta: c.vigencia.tipo === "fechas" ? c.vigencia.hasta : "",
    noIncluye: c.noIncluye,
  };
}

export type Campo = "codigo" | "descuentoPct" | "compraMinima" | "vigencia" | "noIncluye";

const esDia = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);

/** Lo que se puede revisar acá antes de mandar. Al editar, el código no se valida ni se manda: no se cambia. */
export function validarFormulario(f: Formulario, editando: boolean): { ok: true; cupon: CuponParaGuardar } | { ok: false; errores: Partial<Record<Campo, string>> } {
  const errores: Partial<Record<Campo, string>> = {};
  const codigo = normalizarCodigo(f.codigo);
  if (!editando) {
    const p = problemaDelCodigo(codigo);
    if (p) errores.codigo = p;
  }
  const descuentoPct = leerPorcentaje(f.descuentoPct);
  if (descuentoPct === null || !Number.isInteger(descuentoPct) || descuentoPct < 1 || descuentoPct > 100) errores.descuentoPct = "Escribí el descuento: un número entero de 1 a 100.";
  const compraMinima = leerPesos(f.compraMinima);
  if (compraMinima !== null && (!Number.isInteger(compraMinima) || compraMinima < 1)) errores.compraMinima = "Escribí un monto en pesos, sin centavos (por ejemplo 25.000), o dejalo vacío.";
  let vigencia: Vigencia | null = null;
  if (f.tipoVigencia === "dias") {
    const dias = leerPorcentaje(f.dias);
    if (dias === null || !Number.isInteger(dias) || dias < 1 || dias > DIAS_MAX) errores.vigencia = `Los días van de 1 a ${DIAS_MAX}.`;
    else vigencia = { tipo: "dias", dias };
  } else if (!esDia(f.desde) || !esDia(f.hasta)) errores.vigencia = "Elegí el primer y el último día.";
  else if (f.hasta < f.desde) errores.vigencia = "El último día no puede ser antes del primero.";
  else vigencia = { tipo: "fechas", desde: f.desde, hasta: f.hasta };
  const noIncluye = f.noIncluye.trim().replace(/\s+/g, " ");
  if (noIncluye.length > NO_INCLUYE_MAX) errores.noIncluye = `Es muy largo: hasta ${NO_INCLUYE_MAX} letras.`;
  if (Object.keys(errores).length || !vigencia) return { ok: false, errores };
  return {
    ok: true,
    cupon: {
      ...(editando ? {} : { codigo, generado: f.generado }),
      descuentoPct: descuentoPct as number,
      aplicaA: f.aplicaA,
      compraMinima,
      vigencia,
      noIncluye,
    },
  };
}

/**
 * Los avisos mientras se escribe (los mismos que calcula el backend al guardar): se adivina, pasa el máximo (sólo los
 * descuentos sobre el pedido) o ya venció.
 */
export function avisosEnVivo(f: Formulario, tope: number, hoy: string): Aviso[] {
  const avisos: Aviso[] = [];
  const codigo = normalizarCodigo(f.codigo);
  if (codigo && !problemaDelCodigo(codigo) && esAdivinable(codigo)) avisos.push("adivinable");
  const pct = leerPorcentaje(f.descuentoPct);
  if (f.aplicaA === "pedido" && typeof pct === "number" && Number.isInteger(pct) && pct > tope) avisos.push("pasa_el_maximo");
  if (f.tipoVigencia === "fechas" && esDia(f.hasta) && f.hasta < hoy) avisos.push("vencido");
  return avisos;
}

/** «15% del pedido · mínimo $25.000» o «40% del envío · sin mínimo». */
export function textoOferta(c: Pick<Cupon, "descuentoPct" | "aplicaA" | "compraMinima">): string {
  const sobre = c.aplicaA === "envio" ? "del envío" : "del pedido";
  const minimo = c.compraMinima ? `mínimo ${pesos(c.compraMinima)}` : "sin mínimo";
  return `${c.descuentoPct}% ${sobre} · ${minimo}`;
}

/** «Vale 7 días desde que le llega» o «Del 20/10/2026 al 31/10/2026». */
export function textoVigencia(v: Vigencia): string {
  if (v.tipo === "dias") return `Vale ${v.dias} ${v.dias === 1 ? "día" : "días"} desde que le llega`;
  return v.desde === v.hasta ? `Vale sólo el ${fecha(v.desde)}` : `Del ${fecha(v.desde)} al ${fecha(v.hasta)}`;
}

/** En qué lugar de la lista va: los vigentes, los que todavía no empiezan, los vencidos y al final los archivados. */
function lugar(c: Pick<Cupon, "estado" | "vigente" | "vencido">): number {
  if (c.estado === "archivado") return 3;
  if (c.vencido) return 2;
  return c.vigente ? 0 : 1;
}

/**
 * La lista como se muestra: por `lugar` y, adentro, el más nuevo arriba. Así un cupón reactivado vuelve a su lugar sin
 * recargar (antes quedaba al final) y uno vencido no queda arriba de los vigentes. Sin la fecha (un backend anterior),
 * el orden en que llegaron.
 */
export function ordenarCupones<T extends Pick<Cupon, "estado" | "vigente" | "vencido" | "creadoAt">>(cupones: T[]): T[] {
  return cupones
    .map((c, i) => ({ c, i }))
    .sort((a, b) => lugar(a.c) - lugar(b.c) || String(b.c.creadoAt ?? "").localeCompare(String(a.c.creadoAt ?? "")) || a.i - b.i)
    .map((x) => x.c);
}

/** El estado en una palabra, para la etiqueta de cada cupón. */
export function estadoDe(c: Pick<Cupon, "estado" | "vigente" | "vencido" | "vigencia">): { texto: string; tono: "verde" | "rojo" | "ambar" | "gris" } {
  if (c.estado === "archivado") return { texto: "Archivado", tono: "gris" };
  if (c.vencido) return { texto: "Vencido", tono: "rojo" };
  if (!c.vigente && c.vigencia.tipo === "fechas") return { texto: `Empieza el ${fecha(c.vigencia.desde)}`, tono: "ambar" };
  return { texto: "Vigente", tono: "verde" };
}
